import type { AnalyticsClient } from '../../analytics/client';
import type { MatchDiagnosticBundle } from '../../diagnostics/analyzer';
import type { PlayerId } from '../../engine/types';
import type { I18nPort } from '../../i18n/i18n-service';
import { deleteDiagnosticBundle, downloadDiagnosticBundle } from '../../storage/diagnostics-store';
import { requireElement } from '../dom';
import type { ToastController } from './toast-controller';

export interface ResultControllerOptions {
  readonly i18n: I18nPort;
  readonly analytics: AnalyticsClient;
  readonly toast: ToastController;
  readonly onAgain: () => void;
  readonly onReplay: (bundle: MatchDiagnosticBundle) => void;
  readonly onHome: () => void;
}

export class ResultController {
  private readonly screen = requireElement<HTMLElement>('#resultScreen');
  private readonly startScreen = requireElement<HTMLElement>('#startScreen');
  private readonly badge = requireElement<HTMLElement>('#resultBadge');
  private readonly title = requireElement<HTMLElement>('#resultTitle');
  private readonly summary = requireElement<HTMLElement>('#resultSummary');
  private readonly trust = requireElement<HTMLElement>('#resultTrust');
  private readonly diagnosticCounts = requireElement<HTMLElement>('#diagnosticCounts');
  private readonly findings = requireElement<HTMLOListElement>('#findings');
  private readonly exportButton = requireElement<HTMLButtonElement>('#exportButton');
  private readonly replayButton = requireElement<HTMLButtonElement>('#replayButton');
  private readonly deleteButton = requireElement<HTMLButtonElement>('#deleteLogButton');
  private readonly lastBattleButton = requireElement<HTMLButtonElement>('#lastBattleButton');
  private lastBundle: MatchDiagnosticBundle | null = null;
  private lastChecksum: string | null = null;
  private deleteArmedUntil = 0;

  constructor(private readonly options: ResultControllerOptions) {
    this.bindEvents();
    this.options.i18n.subscribe(() => { if (this.screen.classList.contains('open') && this.lastBundle) this.render(this.lastBundle, this.lastChecksum); });
  }

  get bundle(): MatchDiagnosticBundle | null { return this.lastBundle; }

  setLastBundle(bundle: MatchDiagnosticBundle, checksum: string | null): void {
    this.lastBundle = bundle;
    this.lastChecksum = checksum;
    this.lastBattleButton.hidden = false;
  }

  clear(): void {
    this.lastBundle = null;
    this.lastChecksum = null;
    this.lastBattleButton.hidden = true;
    this.exportButton.disabled = true;
    this.replayButton.disabled = true;
  }

  show(bundle: MatchDiagnosticBundle, checksum: string | null = null): void {
    this.setLastBundle(bundle, checksum);
    this.render(bundle, checksum);
    this.screen.classList.add('open');
  }

  hide(): void { this.screen.classList.remove('open'); }

  private bindEvents(): void {
    requireElement<HTMLButtonElement>('#againButton').addEventListener('click', this.options.onAgain);
    this.replayButton.addEventListener('click', () => { if (this.lastBundle) this.options.onReplay(this.lastBundle); });
    this.exportButton.addEventListener('click', () => {
      if (!this.lastBundle) return;
      downloadDiagnosticBundle(this.lastBundle);
      this.options.analytics.event('diagnostics_exported', { matchId: this.lastBundle.manifest.matchId });
    });
    requireElement<HTMLButtonElement>('#homeButton').addEventListener('click', () => {
      this.hide(); this.startScreen.classList.add('open'); this.options.onHome();
    });
    this.deleteButton.addEventListener('click', () => { void this.deleteCurrentLog(); });
    this.lastBattleButton.addEventListener('click', () => { if (this.lastBundle) { this.startScreen.classList.remove('open'); this.show(this.lastBundle, this.lastChecksum); } });
  }

  private render(bundle: MatchDiagnosticBundle, checksum: string | null): void {
    const result = bundle.summary.result;
    if (!result) { this.options.toast.show(this.options.i18n.t('result.noResult')); return; }
    const localSide: PlayerId = bundle.replay.match.localSide;
    const opponentSide: PlayerId = localSide === 'player' ? 'bot' : 'player';
    const labelKey = result.winner === 'draw' ? 'result.draw' : result.winner === localSide ? 'result.victory' : 'result.defeat';
    const label = this.options.i18n.t(labelKey);
    this.badge.textContent = bundle.manifest.matchId.startsWith('replay') ? this.options.i18n.t('result.replayLabel', { label }) : label;
    this.title.textContent = this.options.i18n.t(result.reason === 'king-destroyed' ? 'result.kingDestroyed' : result.reason === 'sudden-death' ? 'result.overtimeVictory' : result.reason === 'tiebreak' ? 'result.tiebreak' : 'result.finished');
    this.summary.textContent = this.options.i18n.t('result.summary', {
      own: result.crowns[localSide], opponent: result.crowns[opponentSide],
      seconds: (result.tick / bundle.replay.rules.tickRate).toFixed(1),
      checksum: checksum ?? bundle.checkpoints.at(-1)?.checksum ?? this.options.i18n.t('result.noChecksum'),
    });
    this.trust.hidden = bundle.manifest.matchMode !== 'network';
    if (!this.trust.hidden) this.trust.textContent = this.options.i18n.t(bundle.manifest.resultTrust === 'server-verified' ? 'network.claimServerVerified' : bundle.manifest.resultTrust === 'peer-confirmed' ? 'network.claimConfirmed' : 'network.claimPending');
    this.diagnosticCounts.textContent = this.options.i18n.t('result.diagnostics', { records: bundle.metrics.records, critical: bundle.summary.critical, errors: bundle.summary.errors, warnings: bundle.summary.warnings });
    this.findings.replaceChildren();
    for (const finding of bundle.findings.slice(0, 30)) {
      const item = document.createElement('li'); item.className = finding.severity;
      item.textContent = this.options.i18n.t('result.finding', { ruleId: finding.ruleId, tick: finding.tickFrom });
      this.findings.append(item);
    }
    if (!bundle.findings.length) {
      const item = document.createElement('li'); item.textContent = this.options.i18n.t('result.noFindings'); this.findings.append(item);
    }
    this.exportButton.disabled = false;
    this.replayButton.disabled = false;
    this.deleteArmedUntil = 0;
    this.deleteButton.disabled = false;
    this.deleteButton.textContent = this.options.i18n.t('result.deleteLog');
  }

  private async deleteCurrentLog(): Promise<void> {
    const bundle = this.lastBundle;
    if (!bundle) return;
    const { i18n, toast } = this.options;
    if (Date.now() > this.deleteArmedUntil) {
      this.deleteArmedUntil = Date.now() + 4_000;
      this.deleteButton.textContent = i18n.t('result.confirmAgain');
      window.setTimeout(() => { if (Date.now() >= this.deleteArmedUntil && !this.deleteButton.disabled) this.deleteButton.textContent = i18n.t('result.deleteLog'); }, 4_050);
      return;
    }
    this.deleteArmedUntil = 0;
    this.deleteButton.disabled = true;
    try {
      await deleteDiagnosticBundle(bundle.manifest.matchId);
      if (this.lastBundle?.manifest.matchId === bundle.manifest.matchId) this.clear();
      this.deleteButton.textContent = i18n.t('result.logDeleted');
      toast.show(i18n.t('result.logDeletedToast'));
    } catch (error) {
      this.deleteButton.textContent = i18n.t('result.logDeleteFailed');
      toast.show(i18n.t('result.logDeleteError', { error: String(error) }));
      window.setTimeout(() => { this.deleteButton.disabled = false; this.deleteButton.textContent = i18n.t('result.deleteLog'); }, 1_800);
    }
  }
}
